import React, { useState, useEffect } from 'react';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import productAPI from '../../services/productApi';
import './Services.css';

const Services = ({ setCurrentPage, setPageData }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPageNum, setCurrentPageNum] = useState(1);

  // Contact information
  const EMAIL = 'Info@transcontinentalnetwork.com';
  const PHONE_NUMBER = '347.668.2160';
  const ADDRESS = '160-04 Cross Bay Blvd. Howard Beach, NY 11414';

  // Navigation handler for service details
  const handleServiceClick = (serviceId) => {
    setPageData({
      itemId: serviceId,
      itemType: 'service'
    });
    setCurrentPage('details');
  };

  // Email contact handler
  const handleEmailContact = () => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  };

  // Phone call handler
  const handlePhoneCall = () => {
    window.location.href = `tel:+13476682160`;
  };

  // Load services from API
  const loadServices = async (categoryFilter = null, page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        per_page: 12,
        page: page
      };

      // Add category filter if selected
      if (categoryFilter && categoryFilter !== 'all') {
        params.category_id = categoryFilter;
      }

      console.log('Loading services with params:', params);

      const response = await productAPI.getServices(params);
      
      if (response && response.success && response.data) {
        console.log('Services loaded successfully:', response.data);
        setServices(response.data.services || []);
        setPagination(response.data.pagination || null);
      } else {
        throw new Error(response?.message || 'Failed to load services');
      }
    } catch (error) {
      console.error('Error loading services:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setError(errorInfo.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    try {
      const response = await productAPI.getCategories({
        active_only: true,
        include_counts: true
      });
      
      if (response && response.success && response.data && response.data.categories) {
        const apiCategories = response.data.categories;
        
        // Enhanced category mapping with comprehensive icons
        const categoryMap = {
          // Security related
          'security cameras': '📹',
          'cameras': '📹',
          'surveillance': '📹',
          'cctv': '📹',
          'video': '📹',
          'ip cameras': '📹',
          
          // NVR/Storage related
          'nvr systems': '💾',
          'nvr': '💾',
          'recording': '💾',
          'storage': '💾',
          'dvr': '💾',
          'network video recorder': '💾',
          
          // Phone systems
          'phone systems': '📞',
          'phones': '📞',
          'voip': '📞',
          'communication': '📞',
          'telephony': '📞',
          'pbx': '📞',
          'voip phones': '📞',
          
          // Access control
          'access control': '🔒',
          'access': '🔒',
          'security': '🔒',
          'door control': '🔒',
          'keypad': '🔒',
          'card reader': '🔒',
          
          // Installation services
          'installation': '🔧',
          'setup': '🔧',
          'mounting': '🔧',
          'wiring': '🔧',
          
          // Monitoring services
          'monitoring': '👁️',
          'surveillance': '👁️',
          'watching': '👁️',
          'guard': '👁️',
          
          // Maintenance
          'maintenance': '⚙️',
          'repair': '⚙️',
          'service': '⚙️',
          'support': '⚙️',
          
          // Networking
          'networking': '🌐',
          'network': '🌐',
          'wifi': '📶',
          'wireless': '📶',
          
          // Accessories
          'accessories': '🔌',
          'equipment': '🔌',
          'hardware': '🔌',
          
          // Cloud services
          'cloud': '☁️',
          'remote': '☁️',
          'online': '☁️'
        };

        // Function to get icon for category
        const getCategoryIcon = (categoryName) => {
          const name = categoryName.toLowerCase();
          
          // Direct match
          if (categoryMap[name]) {
            return categoryMap[name];
          }
          
          // Partial match
          for (const [key, icon] of Object.entries(categoryMap)) {
            if (name.includes(key) || key.includes(name)) {
              return icon;
            }
          }
          
          // Default icon
          return '📋';
        };

        const formattedCategories = [
          { 
            value: 'all', 
            label: 'All Services', 
            icon: '🌐',
            count: apiCategories.reduce((sum, cat) => sum + (cat.service_count || 0), 0)
          },
          ...apiCategories
            .filter(cat => (cat.service_count || 0) > 0)
            .map(cat => ({
              value: cat.id.toString(),
              label: cat.name,
              icon: getCategoryIcon(cat.name),
              count: cat.service_count || 0,
              description: cat.description || null
            }))
        ];

        setCategories(formattedCategories);
      } else {
        throw new Error(response?.message || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      const errorInfo = productAPI.handleAPIError(error);
      
      // Set fallback categories if API fails
      setCategories([
        { 
          value: 'all', 
          label: 'All Services', 
          icon: '🌐',
          count: services.length 
        }
      ]);
      
      console.warn('Categories loaded from fallback due to API error:', errorInfo.message);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCategories();
    loadServices('all', 1);
  }, []);

  // Handle category change
  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setCurrentPageNum(1);
    const categoryId = categoryValue === 'all' ? null : categoryValue;
    loadServices(categoryId, 1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPageNum(page);
    const categoryId = selectedCategory === 'all' ? null : selectedCategory;
    loadServices(categoryId, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Transform service data for ServiceCard
  const transformService = (service) => {
    console.log('Transforming service:', service);
    
    let imageUrl = null;
    
    if (service.images && Array.isArray(service.images) && service.images.length > 0) {
      imageUrl = service.images[0];
    } else if (service.primary_image) {
      imageUrl = service.primary_image;
    } else if (service.image) {
      imageUrl = service.image;
    } else if (service.image_url) {
      imageUrl = service.image_url;
    }

    let categoryName = 'Service';
    if (service.category && typeof service.category === 'object') {
      categoryName = service.category.name;
    } else if (service.category_name) {
      categoryName = service.category_name;
    }

    const isOnSale = service.is_on_sale && service.sale_price && service.sale_price < service.price;
    const currentPrice = isOnSale ? service.sale_price : service.price;
    const discount = isOnSale ? productAPI.calculateDiscount(service.price, service.sale_price) : 0;

    let priceDisplay;
    if (isOnSale) {
      priceDisplay = `Sale: ${productAPI.formatPrice ? productAPI.formatPrice(currentPrice) : `$${currentPrice}`}`;
    } else {
      priceDisplay = `Starting at ${productAPI.formatPrice ? productAPI.formatPrice(currentPrice) : `$${currentPrice}`}`;
    }

    return {
      id: service.id,
      title: service.name || service.title,
      category: categoryName,
      description: service.description || service.short_description || '',
      image: imageUrl,
      price: priceDisplay,
      features: Array.isArray(service.features) ? service.features : [],
      rating: service.rating || 4.5,
      isOnSale: isOnSale,
      originalPrice: isOnSale ? (productAPI.formatPrice ? productAPI.formatPrice(service.price) : `$${service.price}`) : null,
      discount: discount,
      is_available: service.is_available !== false,
      pricing_model: service.pricing_model || 'fixed',
      duration: service.duration || null
    };
  };

  // Loading component
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
      <p>Loading services...</p>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="error-message">
      <div className="error-content">
        <h3>⚠️ Error Loading Services</h3>
        <p>{message}</p>
        <button className="btn btn-primary" onClick={onRetry}>
          Try Again
        </button>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-content">
        <h3>No Services Found</h3>
        <p>
          {selectedCategory === 'all' 
            ? "No services are currently available." 
            : "No services found in this category."}
        </p>
        {selectedCategory !== 'all' && (
          <button 
            className="btn btn-secondary" 
            onClick={() => handleCategoryChange('all')}
          >
            View All Services
          </button>
        )}
      </div>
    </div>
  );

  // Pagination component
  const PaginationControls = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    const currentPage = pagination.page;
    const totalPages = pagination.pages;

    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '0.5rem',
        marginTop: '2rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn btn-secondary"
          style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
        >
          Previous
        </button>

        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`btn ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
            style={{ minWidth: '40px' }}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn btn-secondary"
          style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
        >
          Next
        </button>

        <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Page {currentPage} of {totalPages} ({pagination.total || 0} total services)
        </span>
      </div>
    );
  };

  return (
    <div className="services-page">
      {/* Page Header */}
      <section className="services-header">
        <div className="container">
          <h1 className="page-title fade-in">Professional Security & Communication Solutions</h1>
          <p className="page-subtitle fade-in">
            Expert video surveillance, NVR systems, and phone installations for your home or business. 
            Licensed, insured, and committed to securing your world.
          </p>
        </div>
      </section>

      {/* Service Categories Filter */}
      {categories.length > 1 && (
        <section className="services-filter">
          <div className="container">
            <div className="filter-tabs">
              {categories.map(category => (
                <button
                  key={category.value}
                  className={`filter-tab ${selectedCategory === category.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.value)}
                  disabled={loading}
                >
                  <span className="tab-icon">{category.icon}</span>
                  <span className="tab-label">
                    {category.label}
                    {category.count !== undefined && (
                      <span className="tab-count">({category.count})</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Content */}
      <section className="services-grid section">
        <div className="container">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage 
              message={error} 
              onRetry={() => loadServices(selectedCategory === 'all' ? null : selectedCategory, currentPageNum)} 
            />
          ) : services.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Results count */}
              <div className="results-info" style={{ 
                marginBottom: '1.5rem', 
                textAlign: 'center',
                color: '#666'
              }}>
                <p>
                  Showing {services.length} service{services.length !== 1 ? 's' : ''}
                  {pagination && pagination.total && (
                    <span> of {pagination.total} total</span>
                  )}
                  {selectedCategory !== 'all' && categories.find(c => c.value === selectedCategory) && 
                    ` in ${categories.find(c => c.value === selectedCategory)?.label}`
                  }
                </p>
              </div>

              {/* Services Grid */}
              <div className="grid-2">
                {services.map(service => {
                  const transformedService = transformService(service);
                  
                  return (
                    <ServiceCard 
                      key={service.id} 
                      service={transformedService}
                      onServiceClick={() => handleServiceClick(service.id)}
                    />
                  );
                })}
              </div>

              {/* Pagination */}
              <PaginationControls />
            </>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Need a Custom Security Solution?</h2>
            <p className="cta-text">
              Don't see exactly what you need? Contact us for custom surveillance and communication solutions 
              tailored to your specific security requirements.
            </p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary"
                onClick={handleEmailContact}
              >
                ✉️ Email: {EMAIL}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handlePhoneCall}
              >
                📞 Call Now: {PHONE_NUMBER}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Footer */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '1.5rem',
        textAlign: 'center',
        borderTop: '1px solid #ddd'
      }}>
        <p style={{ margin: '0.5rem 0', color: '#333' }}>
          📍 <strong>{ADDRESS}</strong>
        </p>
        <p style={{ margin: '0.5rem 0', color: '#333' }}>
          📧 <a href={`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'none' }}>{EMAIL}</a> | 
          📞 <a href={`tel:+13476682160`} style={{ color: '#0066cc', textDecoration: 'none' }}>{PHONE_NUMBER}</a>
        </p>
      </div>

      {/* Floating Email Button */}
      <button
        className="email-float"
        onClick={handleEmailContact}
        title="Email us"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#0066cc',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          fontSize: '30px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 16px rgba(0, 102, 204, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
      >
        ✉️
      </button>
    </div>
  );
};

export default Services;